/* eslint-disable no-nested-ternary */
/**
 * @name DataGrid
 * @description Componente común de grid configurable para acciones CRUD
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { EmployeeDetail, LoadingWrapper, NoRegistersFound } from '@components';
import { FiltersService } from '@services';
import { Flex, MUI, Unicons, Typography } from '@styles';

// Material-UI
import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';

// Components
import Actions from './components/actions';
import Header from './components/header';
import Pagination from './components/pagination';
import RowsPerPage from './components/rows-per-page';

class DataGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      order: 'asc',
      orderBy: '',
      currentPage: 0,
      pageSize: 10,
      selectedRows: props.selectedItems ? props.selectedItems : [],
      expandedRows: [],
      newSelectedResp: null
    };
  }

  resetState = () => {
    this.setState({
      order: 'asc',
      orderBy: '',
      selectedRows: [],
      expandedRows: [],
      currentPage: 0,
      newSelectedResp: null
    });
  }

  // *************************************
  // SORT
  // *************************************

  /**
   * Maneja las peticiones de ordenamiento y actualiza el estado
   * @param {object} event
   * @param {string} property { column.id }
   */
  requestSortHandler = (event, property) => {
    const { order, orderBy } = this.state;
    const isAsc = orderBy === property && order === 'asc';
    this.setState({
      order: isAsc ? 'desc' : 'asc',
      orderBy: property
    });
  }

  sort = (array, comparator) => {
    const stabilizedThis = array.map((element, index) => [element, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((element) => element[0]);
  }

  getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => this.descendingComparator(a, b, orderBy)
      : (a, b) => -this.descendingComparator(a, b, orderBy);
  }

  descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // *************************************
  // SELECT
  // *************************************

  /**
   * Selecciona todos o deselecciona todos los row's
   * @param {any} row
   */
  selectAllHandler = (event) => {
    const rows = this.props.rows;

    if (event.target.checked) {
      if (this.state.selectedRows.filter((sRow) => !this.isRowDisabled(sRow)).length > 0) {
        // Deseleccionar seleccionados
        let fRows = rows.filter((row) => this.isRowDisabled(row) && this.isRowSelected(row)); // Filtrar los registros deshabilitados.
        this.props.onSelect && this.props.onSelect(fRows);
        this.setState({ selectedRows: fRows, newSelectedResp: null });
        return;
      } else {
        // Seleccionar todos
        let fRows = rows.filter((row) => !this.isRowDisabled(row)); // Filtrar los registros deshabilitados.
        fRows = [...this.state.selectedRows, ...fRows];
        this.props.onSelect && this.props.onSelect(fRows);
        this.setState({ selectedRows: fRows, newSelectedResp: null });
        return;
      }
    }
    // Deseleccionar todos
    this.props.onSelect && this.props.onSelect([]);
    this.setState({ selectedRows: [], newSelectedResp: null });
  };

  /**
   * Selecciona o deselecciona un row 
   * @param {any} row
   */
  selectRowHandler = (event, row) => {
    const { selectedRows } = this.state;
    const { canSelectMultiple } = this.props;

    const selectedIndex = selectedRows.indexOf(row);

    let newSelected = [];

    if (selectedRows.length > 0 && canSelectMultiple) {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRows, row);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRows.slice(1));
      } else if (selectedIndex === selectedRows.length - 1) {
        newSelected = newSelected.concat(selectedRows.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRows.slice(0, selectedIndex),
          selectedRows.slice(selectedIndex + 1),
        );
      }
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(row);
        this.props.onClickRow && this.props.onClickRow(newSelected);
      } else {
        this.props.onClickRow && this.props.onClickRow([]);
      }
    }

    this.props.onSelect && this.props.onSelect(newSelected);
    this.setState({
      selectedRows: newSelected,
      newSelectedResp: canSelectMultiple ? null : (newSelected.length > 0 ? newSelected : null)
    });
  }

  /**
   * Regresa el row seleccionado
   * @param {any} rowSelected
   * @return {any} row
   */
  clickRowHandler = (rowSelected) => {
    return rowSelected;
  }

  /**
   * Valida que un row esté seleccionado
   * @param {any} row
   * @return {boolean}
   */
  isRowSelected = (row) => {
    const { selectedRows } = this.state;
    return selectedRows.indexOf(row) !== -1;
  }

  /**
   * Valida que un row esté deshabilitado
   * @param {any} row
   * @return {boolean}
   */
  isRowDisabled = (row) => {
    const { disableRowSelectionWhen } = this.props;

    // Si no existe la propiedad para deshabilitar el elemento regresar 'false'.
    if (disableRowSelectionWhen === null || disableRowSelectionWhen === undefined) {
      return false;
    }
    // Si existe la propiedad para deshabilitar el elemento regresar 'true'.
    return row[disableRowSelectionWhen];
  }

  // *************************************
  // PAGINATION
  // *************************************

  changePageSizeHandler = (event) => {
    this.setState({
      pageSize: parseInt(event.target.value, 10),
      currentPage: 0
    });
  };

  changePageHandler = (event, page) => {
    this.setState({ currentPage: page - 1 });
  }

  // *************************************
  // EXPANSION
  // *************************************

  /**
   * Expande un row 
   * @param {any} row
   */
  toggleRowExpansion = (row) => {
    const { expandedRows } = this.state;
    const { canExpandMultiple } = this.props;

    const selectedIndex = expandedRows.indexOf(row);

    let newExpanded = [];

    if (expandedRows.length > 0 && canExpandMultiple) {
      if (selectedIndex === -1) {
        newExpanded = newExpanded.concat(expandedRows, row);
      } else if (selectedIndex === 0) {
        newExpanded = newExpanded.concat(expandedRows.slice(1));
      } else if (selectedIndex === expandedRows.length - 1) {
        newExpanded = newExpanded.concat(expandedRows.slice(0, -1));
      } else if (selectedIndex > 0) {
        newExpanded = newExpanded.concat(
          expandedRows.slice(0, selectedIndex),
          expandedRows.slice(selectedIndex + 1),
        );
      }
    } else {
      if (selectedIndex === -1) {
        newExpanded = newExpanded.concat(row);
        (!this.props.canSelect && this.props.onClickRow) && this.props.onClickRow(newExpanded);
      } else {
        (!this.props.canSelect && this.props.onClickRow) && this.props.onClickRow([]);
      }
    }

    (!this.props.canSelect && this.props.onSelect) && this.props.onSelect(newExpanded);
    this.setState({ expandedRows: newExpanded });
  };

  /**
   * Valida que un row esté expandido
   * @param {any} row
   * @return {boolean}
   */
  isRowExpanded = (row) => {
    const { expandedRows } = this.state;
    return expandedRows.indexOf(row) !== -1;
  }

  render() {
    const {
      columns = [],
      rows = [],
      canAdd,
      canEdit,
      canDelete,
      canCancel,
      canSelect,
      canSelectAll,
      canSelectMultiple,
      canSort,
      canExpand,
      // canExpandMultiple,
      isPaginated,
      onAdd,
      onEdit,
      onDelete,
      onCancel,
      onHandleSwitch,
      showHeader,
      showBackground
    } = this.props;

    let {
      order,
      orderBy,
      currentPage,
      pageSize,
      selectedRows,
      // expandedRows,
      newSelectedResp
    } = this.state;

    pageSize = isPaginated ? pageSize : rows.length;
    currentPage = (rows.length > pageSize) ? currentPage : 0;

    return (
      <>
        <LoadingWrapper area="loading-datagrid">
          <MUI.Box width="100%">
            {(isPaginated || canAdd || canEdit || canDelete || canCancel) && (
              <Flex.Row padding="8px 0" wrap style={{ minHeight: '77px' }}>
                {isPaginated && (
                  <>
                    {/* -- REGISTROS x PÁGINA -- */}
                    <RowsPerPage
                      rowsLength={rows.length}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      onChangePageSize={this.changePageSizeHandler}
                    />
                    {/* -- PÁGINACIÓN -- */}
                    {Math.ceil(rows.length / pageSize) > 1 && (
                      <Pagination
                        rowsLength={rows.length}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        onChangePage={this.changePageHandler}
                        hasActions={canAdd || canEdit || canDelete || canCancel}
                      />
                    )}
                  </>
                )}
                {/* -- ACCIONES -- */}
                {(canAdd || canEdit || canDelete || canCancel) && (
                  <Actions
                    canAdd={canAdd}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    canCancel={canCancel}
                    canSelectMultiple={canSelectMultiple}
                    selectedRows={selectedRows}
                    selectedRowsLength={selectedRows.length}
                    onAdd={onAdd}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onCancel={onCancel}
                  />
                )}
              </Flex.Row>
            )}
            <Styled.TableContainer>
              <Styled.Table style={{
                background: (showBackground) ? '#F1F1F1A1' : 'none',
                borderRadius: (showBackground && (showHeader === false)) ? '6px' : '0px'
              }}
              >
                {/* -- HEADER -- */}
                <Header
                  columns={columns}
                  rowsLength={rows.length}
                  selectedRowsLength={selectedRows.filter((row) => (!this.isRowDisabled(row))).length}
                  canSelect={canSelect}
                  canSelectAll={canSelectAll}
                  canSort={rows.length !== 0 ? canSort : false}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={this.requestSortHandler}
                  onSelectAll={this.selectAllHandler}
                  showHeader={showHeader}
                />
                {/* -- DATAGRID -- */}
                <Styled.TableBody>
                  {this.sort(rows, this.getComparator(order, orderBy))
                    .slice(currentPage * pageSize, currentPage * pageSize + pageSize)
                    .map((row, index) => {
                      const isItemSelected = canSelect ? this.isRowSelected(row) : false;
                      const isItemDisabled = canSelect ? this.isRowDisabled(row) : false;
                      const isExpanded = canExpand ? this.isRowExpanded(row) : false;
                      const labelId = `table-checkbox-${index}`;

                      return (
                        <React.Fragment key={labelId}>
                          <Styled.TableRow
                            key={labelId}
                            role="checkbox"
                            hover={(canSelect && !isItemDisabled) || (!canSelect && canExpand)}
                            aria-checked={isItemSelected}
                            selected={isItemSelected && !isItemDisabled}
                            tabIndex={-1}
                            onClick={(canSelect && !isItemDisabled) ? (event) => this.selectRowHandler(event, row) : (!canSelect && canExpand) ? () => this.toggleRowExpansion(row) : null}
                          >
                            {/* Checkbox */}
                            {canSelect && (
                              <Styled.TableCell padding="checkbox" expanded={isExpanded}>
                                <MUI.Checkbox
                                  checked={isItemSelected}
                                  disabled={isItemDisabled}
                                  inputProps={{ 'aria-labelledby': labelId }}
                                />
                              </Styled.TableCell>
                            )}
                            {/* Registros */}
                            {columns.map((column, idx) => (
                              column.visible && (
                                (column.type === 'complex') ? (
                                  // COMPLEX
                                  <Styled.TableCell
                                    key={`complex-${idx}`}
                                    align={column.align}
                                    expanded={isExpanded}
                                    showHeader={showHeader}
                                  >
                                    <EmployeeDetail
                                      avatar={row[column.id[0]]}
                                      avatarSize="xs"
                                      employeeName={row[column.id[1]]}
                                      employeeDetail={row[column.id[2]]}
                                    />
                                  </Styled.TableCell>
                                ) : (column.type === 'duplex') ? (
                                  // DUPLEX
                                  <Styled.TableCell
                                    key={`duplex-${idx}`}
                                    align={column.align}
                                    expanded={isExpanded}
                                    showHeader={showHeader}
                                  >
                                    <Flex.Col>
                                      <Typography.Text overflow={1}>{row[column.id[0]]}</Typography.Text>
                                      <Typography.Text overflow={1} color="greyDarkAlpha">{row[column.id[1]]}</Typography.Text>
                                    </Flex.Col>
                                  </Styled.TableCell>
                                ) : (column.type === 'switch') ? (
                                  // SWITCH
                                  <Styled.TableCell
                                    key={column.id}
                                    align={column.align}
                                    expanded={isExpanded}
                                    showHeader={showHeader}
                                  >
                                    <MUI.SwitchLabel
                                      name={column.id}
                                      control={(
                                        <MUI.Switch
                                          checked={Boolean(row[column.id])}
                                          onClick={(event) => event.stopPropagation()}
                                          onChange={(event) => {
                                            event.stopPropagation();
                                            onHandleSwitch && onHandleSwitch(event, row);
                                          }}
                                        />
                                      )}
                                    />
                                  </Styled.TableCell>
                                ) : (column.type === 'render') ? (
                                  // RENDER
                                  <Styled.TableCell
                                    key={column.id}
                                    align={column.align}
                                    expanded={isExpanded}
                                    showHeader={showHeader}
                                  >
                                    {column.render(row[column.id], { ...row, isExpanded })}
                                  </Styled.TableCell>
                                ) : (column.type === 'expanded') ? (
                                  // EXPANDED
                                  <Styled.TableCell
                                    key={column.id}
                                    align={column.align}
                                    expanded={isExpanded}
                                    showHeader={showHeader}
                                    style={{ width: '36px' }}
                                  >
                                    <MUI.Tooltip title={i18n.t('GenericMessages.seeMore')}>
                                      <Styled.ButtonIcon color="primary" onClick={() => this.toggleRowExpansion(row)} expanded={isExpanded.toString()}>
                                        <Unicons.ArrowDown size="20" />
                                      </Styled.ButtonIcon>
                                    </MUI.Tooltip>
                                  </Styled.TableCell>
                                ) : (
                                  <Styled.TableCell
                                    key={column.id}
                                    align={column.align}
                                    expanded={isExpanded}
                                    showHeader={showHeader}
                                  >
                                    {newSelectedResp === null ?
                                      column.filter ? FiltersService[column.filter](row[column.id], false) : row[column.id] :
                                      newSelectedResp[0].Id === row.Id ?
                                        column.filter ? FiltersService[column.filter](row[column.id], true) : row[column.id] :
                                        column.filter ? FiltersService[column.filter](row[column.id], false) : row[column.id]}
                                  </Styled.TableCell>
                                )
                              )
                            ))}
                          </Styled.TableRow>
                          {/* Expanded - Accordion */}
                          {canExpand && (
                            <Styled.TableRow>
                              <Styled.TableCell colSpan={columns.length} style={{ padding: '0' }} showHeader={showHeader}>
                                <Styled.Accordion expanded={isExpanded}>
                                  <MuiAccordionSummary style={{ display: 'none' }}>
                                  </MuiAccordionSummary>
                                  <Styled.AccordionDetails>
                                    {columns.map((column) => (
                                      (column.visible && column.type === 'expanded') && (
                                        <div key={column.id} style={{ width: '100%' }}>
                                          {column.render && column.render(row[column.id], row)}
                                        </div>
                                      )
                                    ))}
                                  </Styled.AccordionDetails>
                                </Styled.Accordion>
                              </Styled.TableCell>
                            </Styled.TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                </Styled.TableBody>
              </Styled.Table>
              {/* Not Found */}
              {rows.length === 0 && (
                <NoRegistersFound />
              )}
            </Styled.TableContainer>
          </MUI.Box>
        </LoadingWrapper>
      </>
    );
  }
}

DataGrid.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  // Modelo: [{ type: string, id: string, label: string, visible: boolean, filter: string/null, minWidth: int/null, sort: boolean, align: left/right }
  columns: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['complex', 'duplex', 'switch', 'render']),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
    label: PropTypes.string.isRequired,
    visible: PropTypes.bool.isRequired,
    filter: PropTypes.string,
    width: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
    minWidth: PropTypes.number,
    sort: PropTypes.bool,
    align: PropTypes.oneOf(['left', 'center', 'right'])
  })),
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Boolean que indica si debe ocultar/mostrar el botón de añadir.
  canAdd: PropTypes.bool,
  // Boolean que indica si debe ocultar/mostrar el botón de editar.
  canEdit: PropTypes.bool,
  // Boolean que indica si debe ocultar/mostrar el botón de eliminar.
  canDelete: PropTypes.bool,
  // Boolean que indica si debe ocultar/mostrar el botón de cancelar.
  canCancel: PropTypes.bool,
  // Boolean que indica si se muestran los checkbox para seleccionar elementos.
  canSelect: PropTypes.bool,
  // Boolean que indica si se muestra el checkbox para seleccionar todos los elementos.
  canSelectAll: PropTypes.bool,
  // Boolean que indica si se puede tener una multiple selección de elementos.
  canSelectMultiple: PropTypes.bool,
  // Boolean que indica si se puede ordenar las columnas.
  canSort: PropTypes.bool,
  // Boolean que indica si se puede expandir los elementos.
  canExpand: PropTypes.bool,
  // Boolean que indica si se puede tener una multiple expansión de elementos.
  canExpandMultiple: PropTypes.bool,
  // Boolean que indica si los registros están paginados.
  isPaginated: PropTypes.bool,
  // Arreglo con la colección de elementos seleccionados.
  selectedItems: PropTypes.array,
  // String que permite especificar una columna cuyo estatus servirá para deshabilitar el checkbox de ese registro.
  disableRowSelectionWhen: PropTypes.string,
  // Función a ejecutar cuando se agregará un elemento nuevo, al invocarla es necesario pasar como argumento event.
  onAdd: PropTypes.func,
  // Función a ejecutar cuando se editará un elemento, al invocarla es necesario pasar como argumento event.
  onEdit: PropTypes.func,
  // Función a ejecutar cuando se desea eliminar elementos, al invocarla es necesario pasar como argumento event.
  onDelete: PropTypes.func,
  // Función a ejecutar cuando se desea cancelar elementos, al invocarla es necesario pasar como argumento event.
  onCancel: PropTypes.func,
  // Función a ejecutar cuando se selecciona un elemento.
  onSelect: PropTypes.func,
  // Función a ejecutar cuando se da click un elemento.
  onClickRow: PropTypes.func,
  // Función a ejecutar cuando se da click en un elemento Switch.
  onHandleSwitch: PropTypes.func,
  // Boolean que indica si se debe mostrar el header de las columnas.
  showHeader: PropTypes.bool,
  // Boolean que indica si se debe mostrar un background en 'greyHover'.
  showBackground: PropTypes.bool
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

// Material UI - TableContainer
Styled.TableContainer = styled(MuiTableContainer)`
  width: 100%;
  overflow: auto;
`;

// Material UI - Table
Styled.Table = styled(MuiTable)``;

// Material UI - TableBody
Styled.TableBody = styled(MuiTableBody)``;

// Material UI - TableRow
Styled.TableRow = styled(MuiTableRow)`
  &.MuiTableRow-root {
    transition: background-color 0.25s ease;
  }

  &.MuiTableRow-root.Mui-selected {
    background-color: ${(props) => props.theme.colors.greyHover};
  }

  &.MuiTableRow-root.MuiTableRow-hover:hover {
    background-color: ${(props) => props.theme.colors.greyHover};
    cursor: pointer;
  }
`;

// Material UI - TableCell
Styled.TableCell = styled(MuiTableCell)`
  &.MuiTableCell-root {
    padding: 16px;
    font-family: 'Bryant Pro Regular';
    font-variant: tabular-nums;
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
    vertical-align: middle;
    color: ${(props) => props.theme.colors.greyDark};
    border-bottom: ${(props) => (props.expanded === true) ? 'none' : `1px solid ${props.theme.colors.greyHover}`};
  }

  &.MuiTableCell-paddingCheckbox {
    padding: 4px;
  }
`;

Styled.Accordion = styled(MuiAccordion)`
  &.MuiAccordion-root {
    background: none;
    box-shadow: none;
    width: 100%;
  }

  &.MuiAccordion-root:before {
    background: none;
  }
  
  &.MuiAccordion-root.Mui-expanded {
    margin: 0;
  }
`;

Styled.AccordionDetails = styled(MuiAccordionDetails)`
  &.MuiAccordionDetails-root {
    padding: 0px;
    width: 100%;
  }
`;

Styled.ButtonIcon = styled(MUI.ButtonIcon)`
  transform: ${(props) => props.expanded === 'true' ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
`;

export default DataGrid;
